import { useEffect, useRef, useState } from "react";
import { listWorkerProfiles } from "../workerProfiles/workerProfilesApi.js";

const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 8;

// Only the one real WorkerProfileStatus this codebase's own P097/P097R
// backend contract issues today (see WorkerProfilesPage.jsx's own
// WORKER_STATUS_LABELS) - never inventing an "Active"/"Working" label that
// doesn't exist on the real DTO.
const WORKER_STATUS_LABELS = { ProfileCreated: "สร้างโปรไฟล์แล้ว" };

function workerStatusLabel(code) {
  return WORKER_STATUS_LABELS[code] ?? code ?? "-";
}

function describeSearchError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ค้นหาแรงงานในขอบเขตนี้";
  }
  return "ไม่สามารถค้นหาแรงงานได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * Real, server-searched worker picker modal for Footstep History (Prompt
 * 143). Same real read API AgentFootstepsPage.jsx already uses for its own
 * (Agent-scoped) worker typeahead - ../workerProfiles/workerProfilesApi.js's
 * listWorkerProfiles, with `search` forwarded verbatim and every other
 * result field (agent/line/status) rendered exactly as returned, never
 * invented. Visibility scoping (which workers this actor can even see)
 * happens entirely on the backend from the caller's own session, same as
 * every other list in this app - this dialog adds no scoping of its own.
 */
/**
 * Maps one real worker-profile list item into the shape this page's
 * selected-worker state uses. Exported so the page's default-worker
 * bootstrap builds its selection through exactly the same mapping as a
 * human picking from this dialog - one definition, no drift.
 */
export function toSelectedWorker(item) {
  return {
    workerId: item.workerId,
    workerCode: item.workerCode,
    name: item.worker?.fullNameNative || item.worker?.fullNameEnglish || item.workerCode,
    agentCode: item.agent?.agentCode ?? null,
    agentName: item.agent?.displayName ?? null,
    superAgentName: item.line?.superAgentName ?? null,
    plcName: item.line?.plcName ?? null,
    workerStatus: item.workerStatus,
  };
}

export default function WorkerSearchDialog({ open, onClose, onSelect }) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatus] = useState("idle");
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const dialogRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setQuery("");
    setDebouncedQuery("");
    setItems([]);
    setStatus("idle");
    setError(null);
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(focusTimer);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let cancelled = false;
    setStatus("loading");
    setError(null);
    listWorkerProfiles({ search: debouncedQuery, page: 1, pageSize: PAGE_SIZE })
      .then((data) => {
        if (!cancelled) {
          setItems(data?.items ?? []);
          setStatus("ready");
        }
      })
      .catch((requestError) => {
        if (!cancelled) {
          setError(requestError);
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  function handleBackdropClick(event) {
    if (dialogRef.current && !dialogRef.current.contains(event.target)) {
      onClose();
    }
  }

  function pick(item) {
    onSelect(toSelectedWorker(item));
  }

  return (
    <div className="wx-fsh-dialog-root" onMouseDown={handleBackdropClick}>
      <div className="wx-fsh-dialog-backdrop" aria-hidden="true" />
      <div ref={dialogRef} className="wx-fsh-dialog" role="dialog" aria-modal="true" aria-label="เลือกแรงงาน">
        <div className="wx-fsh-dialog-head">
          <h2>เลือกแรงงาน</h2>
          <button type="button" className="wx-fsh-dialog-close" onClick={onClose} aria-label="ปิด">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        <input
          ref={inputRef}
          type="search"
          className="wx-fsh-dialog-search"
          placeholder="ค้นหาชื่อ, รหัสแรงงาน หรือเบอร์โทร..."
          aria-label="ค้นหาแรงงาน"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        <div className="wx-fsh-dialog-results">
          {status === "loading" && items.length === 0 && <p className="wx-fsh-dialog-state">กำลังค้นหา...</p>}
          {status === "error" && <p className="wx-fsh-dialog-state wx-fsh-dialog-state--error">{describeSearchError(error)}</p>}
          {status === "ready" && items.length === 0 && (
            <p className="wx-fsh-dialog-state">{debouncedQuery ? "ไม่พบแรงงานที่ตรงกับคำค้นหา" : "ไม่พบแรงงานในขอบเขตที่มองเห็น"}</p>
          )}
          {items.length > 0 && (
            <ul className="wx-fsh-dialog-list">
              {items.map((item) => {
                const fullName = item.worker?.fullNameNative || item.worker?.fullNameEnglish || "-";
                return (
                  <li key={item.workerId} className="wx-fsh-dialog-row">
                    <div className="wx-fsh-dialog-row-main">
                      <strong>{fullName}</strong>
                      <span className="wx-fsh-dialog-row-code">{item.workerCode}</span>
                    </div>
                    <div className="wx-fsh-dialog-row-meta">
                      <span>{item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}</span>
                      <span className="wx-badge wx-badge--neutral">{workerStatusLabel(item.workerStatus)}</span>
                    </div>
                    <button type="button" className="wx-button wx-button--primary wx-fsh-dialog-pick-button" onClick={() => pick(item)}>
                      เลือก
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
